/**
 * FormSelectionField is a multi-select (checkboxes) or single-select (radio buttons) field for forms.
 * Users can add up to 50 options, reorder them, and set defaults.
 */

import { type InputHTMLAttributes, useEffect, useRef } from 'react';
import { useNode, type Node, ROOT_NODE, useEditor } from '@craftjs/core';
import { cx } from '@emotion/css';
import type { NodeCustomProps } from '../../../types/craft';
import { useRenderContext } from '../../../context/RenderContext';
import { FORM_SELECTION_FIELD_NODE_NAME } from '../../../utils/constants';
import { generateUuidV4 } from '../form-utils';
import { getBreakpointProp } from '../../../utils/breakpointUtils';
import { getHelpTextStyle } from './fieldStyles';

type FormSelectionFieldBaseProps = {
  // Standard field properties
  id: string;
  label: string;
  inputType: 'selection';
  required: boolean;
  hidden: boolean;

  // Selection-specific properties
  placeholder: string; // "Select one or more options:"
  options: string[]; // Array of option values (strings)
  defaultIndices: number[]; // Indices of default options - handles duplicate names
  isSingleSelect: boolean; // false = checkboxes (multi), true = radio buttons (single)

  // Help text
  helpText: string;
  showHelpText: boolean;
};

type FormSelectionFieldBreakpointProps = Partial<FormSelectionFieldBaseProps>;

export type FormSelectionFieldProps = FormSelectionFieldBaseProps & {
  tablet?: FormSelectionFieldBreakpointProps;
  mobile?: FormSelectionFieldBreakpointProps;
};

export const defaultProps: FormSelectionFieldProps = {
  id: '',
  label: 'Selection',
  inputType: 'selection',
  required: false,
  hidden: false,
  placeholder: 'Select one or more options:',
  options: ['Option one', 'Option two', 'Option three', 'Option four'],
  defaultIndices: [],
  isSingleSelect: false, // Checkboxes by default
  helpText: '',
  showHelpText: false,
};

const FormSelectionField = (props: Partial<FormSelectionFieldProps>) => {
  const mergedProps: FormSelectionFieldProps = { ...defaultProps, ...props };
  const { isEditable, environment } = useRenderContext();

  const {
    connectors: { connect, drag },
    id: craftNodeId,
  } = useNode((node: Node) => ({
    parentId: node.data.parent as string | null,
    isLocked: node.data.custom?.isLocked,
  }));

  const { actions: editorActions, breakpoint } = useEditor((_, query) => {
    const rootNode = query.node(ROOT_NODE).get();
    return {
      breakpoint: rootNode?.data.props.breakpoint || 'desktop',
    };
  });

  // Read props using breakpoint-aware getter
  const id = getBreakpointProp(mergedProps, 'id', breakpoint);
  const required = getBreakpointProp(mergedProps, 'required', breakpoint);
  const hidden = getBreakpointProp(mergedProps, 'hidden', breakpoint);
  const placeholder = getBreakpointProp(mergedProps, 'placeholder', breakpoint);
  const options = getBreakpointProp(mergedProps, 'options', breakpoint);
  const defaultIndices = getBreakpointProp(mergedProps, 'defaultIndices', breakpoint);
  const isSingleSelect = getBreakpointProp(mergedProps, 'isSingleSelect', breakpoint);
  const helpText = getBreakpointProp(mergedProps, 'helpText', breakpoint);
  const showHelpText = getBreakpointProp(mergedProps, 'showHelpText', breakpoint);

  // Generate a unique ID if one isn't set (using useEffect to avoid render-time state updates)
  const hasGeneratedId = useRef(false);
  useEffect(() => {
    if (!id && !hasGeneratedId.current) {
      hasGeneratedId.current = true;
      const nodeId = generateUuidV4();
      editorActions.history.ignore().setProp(craftNodeId, (props: FormSelectionFieldProps) => {
        props.id = nodeId;
      });
    }
  }, [id, editorActions, craftNodeId]);

  const isServerRender = environment === 'server';
  const inputType = isSingleSelect ? 'radio' : 'checkbox';
  // Use id or 'temp' as fallback during initial render before useEffect sets the id
  const fieldId = id || 'temp';
  // For radio buttons: all options in this field share the same name (using CraftJS node ID for uniqueness across fields)
  // For checkboxes: inputName is undefined, so each option falls back to its own optionId as the name
  const inputName = isSingleSelect ? `selection-field-${craftNodeId}` : undefined;

  return (
    <div
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cx(
        'field-node',
        'field-node--selection',
        isEditable && 'node-dashed-indicator styled-node',
        'boundary-indicator',
        hidden && 'field-node--hidden',
      )}
      data-field-id={fieldId}
      aria-hidden={hidden || undefined}
    >
      {/* Placeholder text */}
      {placeholder && (
        <p className="field-selection-placeholder">
          {placeholder}
          {required && (
            <span aria-hidden="true" className="field-required-indicator">
              *
            </span>
          )}
        </p>
      )}

      {/* Options - key forces remount when defaults change to reset defaultChecked */}
      <div
        className="field-selection-wrapper"
        key={`${isEditable ? 'edit' : 'view'}-${isSingleSelect}-${defaultIndices?.join(',') ?? ''}`}
      >
        {options.map((option, index) => {
          // Use CraftJS node ID to ensure unique option IDs even for duplicated fields
          const optionId = `${craftNodeId}-option-${index}`;
          // For single-select: only one default (first in defaultIndices)
          // For multi-select: check if index is in defaultIndices
          let isDefault = false;
          if (isSingleSelect) {
            isDefault = defaultIndices?.length > 0 && defaultIndices[0] === index;
          } else {
            isDefault = defaultIndices?.includes(index) ?? false;
          }

          const inputProps = buildInputProps(
            optionId,
            inputName || optionId,
            inputType,
            required,
            isDefault,
            isServerRender,
            fieldId,
            showHelpText,
            helpText,
          );

          return (
            <div key={index} className="field-selection-option">
              <input {...inputProps} />
              <label htmlFor={optionId} className="field-selection-text">
                {option}
              </label>
            </div>
          );
        })}
      </div>

      {/* Help text */}
      <div
        className={cx(
          'field-helper-text-wrapper',
          getHelpTextStyle({ showHelpText, helpText } as FormSelectionFieldProps),
        )}
      >
        <p id={`${fieldId}-helper-text`} className="field-helper-text">
          {showHelpText && helpText && helpText.trim().length > 0 ? helpText : '\u200B'}
        </p>
      </div>

      {/* Required indicator (visually hidden, for screen readers) */}
      {/* {required && <span className="sr-only">This field is required</span>} */}
    </div>
  );
};

/**
 * Builds input attributes for checkbox or radio input element.
 */
function buildInputProps(
  id: string,
  name: string,
  type: 'checkbox' | 'radio',
  required: boolean,
  isDefault: boolean,
  isServerRender: boolean,
  fieldId: string,
  showHelpText: boolean,
  helpText: string,
): InputHTMLAttributes<HTMLInputElement> {
  const attributes: InputHTMLAttributes<HTMLInputElement> = {
    id,
    name,
    type,
    className: 'field-selection-input',
    required,
    'aria-required': required,
    'aria-describedby': showHelpText && helpText ? `${fieldId}-helper-text` : undefined,
    // Use checked for server render, defaultChecked for client (same as consent field)
    ...(isServerRender ? isDefault && { checked: true } : isDefault && { defaultChecked: true }),
  };

  (attributes as Record<string, unknown>)['data-required'] = required || undefined;

  return attributes;
}

FormSelectionField.craft = {
  displayName: FORM_SELECTION_FIELD_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: 'Selection',
    isLocked: false,
  } as NodeCustomProps,
};

export default FormSelectionField;
